import { Link } from 'react-router-dom';
import SiteLayout from './SiteLayout';
import {
  IconArrow,
  IconCloud, IconInfra, IconK8s, IconPipeline, IconObs, IconTerminalSq,
  IconHands, IconRoute,
} from './icons';

// Mindset P.E — Pensamento de Engenheiro (conceito-assinatura)
const PILLARS = [
  { ic: <IconObs />, t: 'Entenda o porquê', d: 'Nada de decorar comando. Você aprende o que acontece por baixo pra decidir com segurança.' },
  { ic: <IconTerminalSq />, t: 'Resolva problemas', d: 'Engenharia é resolver, não seguir receita. A gente treina seu raciocínio, não só a sintaxe.' },
  { ic: <IconRoute />, t: 'Autonomia', d: 'Ler documentação, debugar, escolher a ferramenta certa. Você aprende a se virar sozinho.' },
  { ic: <IconHands />, t: 'Prática acima de tudo', d: 'A mão no teclado ensina o que slide nenhum ensina. Todo módulo tem lab e projeto.' },
];

// Trilha de carreira — do estágio ao sênior
const LADDER = [
  { n: '01', t: 'Estágio', tag: 'primeiros passos', desc: 'Terminal, Git e o básico de nuvem com acompanhamento. Aqui o objetivo é ganhar confiança.' },
  { n: '02', t: 'Júnior', tag: 'entrega real', desc: 'Fundamentos sólidos de Linux, redes e cloud. Você já resolve tarefas de verdade.' },
  { n: '03', t: 'Pleno', tag: 'autonomia', desc: 'Infra como código, pipelines e automação. Você resolve problemas sem depender de ninguém.' },
  { n: '04', t: 'Sênior', tag: 'visão de sistema', desc: 'Arquitetura, decisões técnicas e mentoria. Você enxerga o sistema inteiro.' },
];

// Trilhas técnicas
const TRACKS = [
  { ic: <IconCloud />, t: 'AWS', d: 'Compute, redes, IAM, S3 e serverless. A base da nuvem que o mercado realmente usa.', m: 'nuvem' },
  { ic: <IconInfra />, t: 'Terraform', d: 'Infraestrutura como código: módulos, state, workspaces e boas práticas de time.', m: 'IaC' },
  { ic: <IconK8s />, t: 'Kubernetes', d: 'Do pod ao cluster: deploy, scaling, ingress e troubleshooting sem pânico.', m: 'orquestração' },
  { ic: <IconPipeline />, t: 'CI/CD', d: 'Pipelines que buildam, testam e entregam — sem susto e sem sexta-feira tensa.', m: 'entrega contínua' },
  { ic: <IconObs />, t: 'Observabilidade', d: 'Logs, métricas e traces. Enxergar o sistema antes do incidente te encontrar.', m: 'SRE' },
  { ic: <IconTerminalSq />, t: 'Linux & Redes', d: 'O terminal sem medo: shell, permissões, processos e TCP/IP na veia.', m: 'fundamentos' },
];

export default function CommercialHome() {
  return (
    <SiteLayout>
      {/* ---------------- HERO ---------------- */}
      <section className="cc-hero">
        <div className="cc-hero-orb o1" />
        <div className="cc-hero-orb o2" />
        <div className="cc-wrap cc-hero-grid">
          <div className="cc-hero-copy">
            <span className="cc-eyebrow">mindset p.e · pensamento de engenheiro</span>
            <h1 className="cc-h1">Do estágio ao <span className="cc-serif">sênior</span>.</h1>
            <p className="cc-hero-sub">
              A Cloud Café &amp; DevOps guia quem está começando em tecnologia até virar sênior —
              desenvolvendo o <strong>Pensamento de Engenheiro</strong> na prática, de forma simples e sem burocracia.
            </p>
            <div className="cc-hero-actions">
              <Link className="cc-btn cc-btn-primary" to="/treinamentos/cadastro">Começar grátis <IconArrow /></Link>
              <Link className="cc-btn cc-btn-ghost" to="/treinamentos">Já sou aluno</Link>
            </div>
            <div className="cc-hero-tags">
              {['aws', 'terraform', 'kubernetes', 'ci/cd', 'linux', 'observabilidade'].map((t) => (
                <span className="cc-tag" key={t}>{t}</span>
              ))}
            </div>
          </div>

          {/* assinatura: terminal executando um deploy */}
          <div className="cc-term-wrap">
            <div className="cc-steam"><span /><span /><span /></div>
            <div className="cc-terminal" role="img" aria-label="Terminal executando um deploy: infraestrutura, build, testes e produção concluídos.">
              <div className="cc-term-bar">
                <span className="cc-dot r" /><span className="cc-dot y" /><span className="cc-dot g" />
                <span className="cc-term-title">~/cloudcafe — deploy</span>
              </div>
              <div className="cc-term-body">
                <span className="ln"><span className="cc-c-prompt">$</span> <span className="cc-c-cmd">cloudcafe deploy --trilha aws</span></span>
                <span className="ln"><span className="cc-c-mut">›</span> provisionando a infra ....... <span className="cc-c-ok">ok</span></span>
                <span className="ln"><span className="cc-c-mut">›</span> build da imagem ............. <span className="cc-c-ok">ok</span></span>
                <span className="ln"><span className="cc-c-mut">›</span> rodando os testes ........... <span className="cc-c-ok">ok</span></span>
                <span className="ln"><span className="cc-c-run">»</span> deploy em produção .......... <span className="cc-c-ok">ok</span></span>
                <span className="ln"><span className="cc-c-ok">✔</span> no ar em 42s.<span className="cc-cursor" /></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MINDSET P.E ---------------- */}
      <section className="cc-section" id="mindset">
        <div className="cc-wrap cc-split">
          <div className="cc-reveal">
            <span className="cc-eyebrow">o coração da escola</span>
            <h2 className="cc-h2">Mindset P.E — Pensamento de Engenheiro</h2>
            <p className="cc-lead" style={{ marginTop: 16 }}>
              Mais do que ferramentas, a gente forma um jeito de pensar. É o que separa quem
              executa tarefa de quem resolve problema — e o que te leva de estagiário a sênior.
            </p>
          </div>
          <div className="cc-feats">
            {PILLARS.map((p) => (
              <div className="cc-feat cc-reveal" key={p.t}>
                <div className="cc-feat-ic">{p.ic}</div>
                <div>
                  <h3>{p.t}</h3>
                  <p>{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TRILHA DE CARREIRA ---------------- */}
      <section className="cc-section" id="carreira">
        <div className="cc-wrap">
          <div className="cc-pipe-head cc-reveal">
            <div>
              <span className="cc-eyebrow">sua evolução</span>
              <h2 className="cc-h2">Um caminho do estágio ao sênior</h2>
            </div>
            <p className="cc-lead">Guiado e na ordem certa. Você sempre sabe onde está e qual o próximo passo — sem se perder e sem burocracia.</p>
          </div>
          <div className="cc-pipe">
            {LADDER.map((s) => (
              <div className="cc-stage cc-reveal" key={s.n}>
                <div className="cc-stage-n">{s.n}</div>
                <div className="cc-stage-coffee">{s.t}</div>
                <div className="cc-stage-map">{s.tag}</div>
                <p className="cc-stage-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TRILHAS TÉCNICAS ---------------- */}
      <section className="cc-section" id="trilhas">
        <div className="cc-wrap">
          <div className="cc-reveal">
            <span className="cc-eyebrow">trilhas técnicas</span>
            <h2 className="cc-h2">As ferramentas que o mercado usa</h2>
            <p className="cc-lead" style={{ marginTop: 14 }}>Seis trilhas que se conectam. A teoria mínima e a prática que importa — no seu ritmo.</p>
          </div>
          <div className="cc-tracks">
            {TRACKS.map((t) => (
              <article className="cc-track cc-reveal" key={t.t}>
                <div className="cc-track-ic">{t.ic}</div>
                <h3>{t.t}</h3>
                <p>{t.d}</p>
                <div className="cc-track-meta">{t.m}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="cc-section" style={{ paddingTop: 0 }}>
        <div className="cc-wrap">
          <div className="cc-cta cc-reveal">
            <h2>Comece sua evolução hoje.</h2>
            <p>Crie sua conta grátis e dê o primeiro passo do estágio ao sênior. Simples, prático e sem burocracia.</p>
            <div className="cc-cta-actions">
              <Link className="cc-btn cc-btn-primary" to="/treinamentos/cadastro">Criar conta grátis <IconArrow /></Link>
              <Link className="cc-btn cc-btn-ghost" to="/treinamentos/planos">Ver planos</Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
